import {createSlice, PayloadAction} from "@reduxjs/toolkit";

import {UserSource} from "../../tracking/tracking_utils/user_source";
import {AuthModalVariant} from "../components/auth_modal/AuthModalHeaderVariants";
import {AuthActionType} from "../types/AuthActionType";
import {IAuthModalVariant} from "./auth_modal_slice";
import {IRegistrationFlowPreferences} from "./registration_flow_slice";

export enum RegistrationFlowStepVariantB {
    REGISTRATION = "REGISTRATION",
    PREFERENCES = "PREFERENCES",
    PHONE_COMPLETION = "PHONE_COMPLETION",
    PHONE_VERIFICATION = "PHONE_VERIFICATION",
    UPDATED_PREFERENCES = "UPDATED_PREFERENCES"
}

export type RegistrationMethod = "email" | "social";
export type SocialMediaType = "google" | "facebook";

interface IRegistrationFlowState {
    isActive: boolean;
    registrationMethod: RegistrationMethod;
    currentStep: RegistrationFlowStepVariantB;
    isUserLoggedIn?: boolean;
    hasCompleteProfile?: boolean;
    hasPhoneVerified?: boolean;
    socialMediaType?: SocialMediaType;
    userSource?: UserSource;
    gtmSourceSection?: string;
    preferences: IRegistrationFlowPreferences | null;
    modalVariant: IAuthModalVariant;
    lastAuthAction: AuthActionType | null;
    propertyId?: number;
    offerId?: number;
    titleVariant?: AuthModalVariant;
    showLoginForm?: boolean;
    registrationSuccessCallback?: () => void;
}

const initialState: IRegistrationFlowState = {
    isActive: false,
    registrationMethod: "email",
    currentStep: RegistrationFlowStepVariantB.REGISTRATION,
    isUserLoggedIn: false,
    hasPhoneVerified: false,
    hasCompleteProfile: false,
    userSource: undefined,
    gtmSourceSection: undefined,
    preferences: null,
    modalVariant: "hidden_prices_variant_b",
    propertyId: undefined,
    offerId: undefined,
    lastAuthAction: null,
    titleVariant: AuthModalVariant.HIDDEN_PRICES_VARIANT_B,
    showLoginForm: undefined,
    registrationSuccessCallback: undefined
};

export interface IStartRegistrationFlowPayload {
    isUserLoggedIn?: boolean;
    hasCompleteProfile?: boolean;
    hasPhoneVerified?: boolean;
    registrationMethod?: RegistrationMethod;
    socialMediaType?: SocialMediaType;
    userSource?: UserSource;
    gtmSourceSection?: string;
    modalVariant?: IAuthModalVariant;
    propertyId?: number;
    offerId?: number;
    titleVariant?: AuthModalVariant;
    showLoginForm?: boolean;
    registrationSuccessCallback?: () => void;
}

export const registrationFlowVariantBSlice = createSlice({
    name: "registrationFlowVariantB",
    initialState,
    reducers: {
        startRegistrationFlowVariantB: (state, action: PayloadAction<IStartRegistrationFlowPayload>) => {
            state.isActive = true;
            state.isUserLoggedIn = action.payload.isUserLoggedIn;
            state.hasCompleteProfile = action.payload.hasCompleteProfile;
            state.hasPhoneVerified = action.payload.hasPhoneVerified;
            state.socialMediaType = action.payload.socialMediaType;
            state.userSource = action.payload.userSource;
            state.gtmSourceSection = action.payload.gtmSourceSection;
            state.propertyId = action.payload.propertyId;
            state.offerId = action.payload.offerId;
            state.titleVariant = action.payload.titleVariant;
            state.showLoginForm = action.payload.showLoginForm;
            state.registrationSuccessCallback = action.payload.registrationSuccessCallback;
            // Determine starting step based on whether profile completion is needed
            if (!action.payload.isUserLoggedIn) {
                state.currentStep = RegistrationFlowStepVariantB.REGISTRATION;
            } else if (!action.payload.hasCompleteProfile) {
                state.currentStep = RegistrationFlowStepVariantB.PHONE_COMPLETION;
            } else if (!action.payload.hasPhoneVerified) {
                state.currentStep = RegistrationFlowStepVariantB.PHONE_VERIFICATION;
            }
        },
        nextStepVariantB: (state, action: PayloadAction<{hasCompleteProfile: boolean; hasPhoneVerified: boolean}>) => {
            switch (state.currentStep) {
                case RegistrationFlowStepVariantB.REGISTRATION:
                    if (!action.payload.hasCompleteProfile) {
                        state.currentStep = RegistrationFlowStepVariantB.PHONE_COMPLETION;
                    } else if (!action.payload.hasPhoneVerified) {
                        state.currentStep = RegistrationFlowStepVariantB.PHONE_VERIFICATION;
                    } else if (state.lastAuthAction === AuthActionType.LOGIN) {
                        return initialState;
                    } else {
                        state.currentStep = RegistrationFlowStepVariantB.PREFERENCES;
                    }
                    break;
                case RegistrationFlowStepVariantB.PHONE_COMPLETION:
                    if (!state.hasPhoneVerified) {
                        state.currentStep = RegistrationFlowStepVariantB.PHONE_VERIFICATION;
                    } else {
                        state.currentStep = RegistrationFlowStepVariantB.PREFERENCES;
                    }
                    break;
                case RegistrationFlowStepVariantB.PHONE_VERIFICATION:
                    state.currentStep = RegistrationFlowStepVariantB.PREFERENCES;
                    break;
                case RegistrationFlowStepVariantB.PREFERENCES:
                    state.currentStep = RegistrationFlowStepVariantB.UPDATED_PREFERENCES;
                    break;
                default:
                    state.isActive = false;
            }
        },
        setStepVariantB: (state, action: PayloadAction<{step: RegistrationFlowStepVariantB}>) => {
            state.currentStep = action.payload.step;
        },
        savePreferencesVariantB: (state, action: PayloadAction<IRegistrationFlowPreferences>) => {
            state.preferences = action.payload;
        },
        closeFlowVariantB: () => {
            return initialState;
        },
        setLastAuthActionVariantB: (state, action: PayloadAction<{authAction: AuthActionType}>) => {
            const {authAction} = action.payload;
            state.lastAuthAction = authAction;
        },
        setRegistrationMethodVariantB: (state, action: PayloadAction<{method: RegistrationMethod}>) => {
            state.registrationMethod = action.payload.method;
        },
        setPropertyAndOfferIds: (state, action: PayloadAction<{propertyId?: number; offerId?: number}>) => {
            state.propertyId = action.payload.propertyId;
            state.offerId = action.payload.offerId;
        }
    }
});

export const {
    startRegistrationFlowVariantB,
    nextStepVariantB,
    setStepVariantB,
    savePreferencesVariantB,
    closeFlowVariantB,
    setLastAuthActionVariantB,
    setRegistrationMethodVariantB,
    setPropertyAndOfferIds
} = registrationFlowVariantBSlice.actions;
import {createSlice, PayloadAction} from "@reduxjs/toolkit";

import {UserSource} from "../../tracking/tracking_utils/user_source";
import {AuthModalVariant} from "../components/auth_modal/AuthModalHeaderVariants";
import {AuthActionType} from "../types/AuthActionType";
import {IAuthModalVariant} from "./auth_modal_slice";
import {IRegistrationFlowPreferences} from "./registration_flow_slice";

export enum RegistrationFlowStepVariantB {
    REGISTRATION = "REGISTRATION",
    PREFERENCES = "PREFERENCES",
    PHONE_COMPLETION = "PHONE_COMPLETION",
    PHONE_VERIFICATION = "PHONE_VERIFICATION",
    UPDATED_PREFERENCES = "UPDATED_PREFERENCES"
}

export type RegistrationMethod = "email" | "social";
export type SocialMediaType = "google" | "facebook";

interface IRegistrationFlowState {
    isActive: boolean;
    registrationMethod: RegistrationMethod;
    currentStep: RegistrationFlowStepVariantB;
    isUserLoggedIn?: boolean;
    hasCompleteProfile?: boolean;
    hasPhoneVerified?: boolean;
    socialMediaType?: SocialMediaType;
    userSource?: UserSource;
    gtmSourceSection?: string;
    preferences: IRegistrationFlowPreferences | null;
    modalVariant: IAuthModalVariant;
    lastAuthAction: AuthActionType | null;
    propertyId?: number;
    offerId?: number;
    titleVariant?: AuthModalVariant;
    showLoginForm?: boolean;
    registrationSuccessCallback?: () => void;
}

const initialState: IRegistrationFlowState = {
    isActive: false,
    registrationMethod: "email",
    currentStep: RegistrationFlowStepVariantB.REGISTRATION,
    isUserLoggedIn: false,
    hasPhoneVerified: false,
    hasCompleteProfile: false,
    userSource: undefined,
    gtmSourceSection: undefined,
    preferences: null,
    modalVariant: "hidden_prices_variant_b",
    propertyId: undefined,
    offerId: undefined,
    lastAuthAction: null,
    titleVariant: AuthModalVariant.HIDDEN_PRICES_VARIANT_B,
    showLoginForm: undefined,
    registrationSuccessCallback: undefined
};

export interface IStartRegistrationFlowPayload {
    isUserLoggedIn?: boolean;
    hasCompleteProfile?: boolean;
    hasPhoneVerified?: boolean;
    registrationMethod?: RegistrationMethod;
    socialMediaType?: SocialMediaType;
    userSource?: UserSource;
    gtmSourceSection?: string;
    modalVariant?: IAuthModalVariant;
    propertyId?: number;
    offerId?: number;
    titleVariant?: AuthModalVariant;
    showLoginForm?: boolean;
    registrationSuccessCallback?: () => void;
}

export const registrationFlowVariantBSlice = createSlice({
    name: "registrationFlowVariantB",
    initialState,
    reducers: {
        startRegistrationFlowVariantB: (state, action: PayloadAction<IStartRegistrationFlowPayload>) => {
            state.isActive = true;
            state.isUserLoggedIn = action.payload.isUserLoggedIn;
            state.hasCompleteProfile = action.payload.hasCompleteProfile;
            state.hasPhoneVerified = action.payload.hasPhoneVerified;
            state.socialMediaType = action.payload.socialMediaType;
            state.userSource = action.payload.userSource;
            state.gtmSourceSection = action.payload.gtmSourceSection;
            state.propertyId = action.payload.propertyId;
            state.offerId = action.payload.offerId;
            state.titleVariant = action.payload.titleVariant;
            state.showLoginForm = action.payload.showLoginForm;
            state.registrationSuccessCallback = action.payload.registrationSuccessCallback;
            // Determine starting step based on whether profile completion is needed
            if (!action.payload.isUserLoggedIn) {
                state.currentStep = RegistrationFlowStepVariantB.REGISTRATION;
            } else if (!action.payload.hasCompleteProfile) {
                state.currentStep = RegistrationFlowStepVariantB.PHONE_COMPLETION;
            } else if (!action.payload.hasPhoneVerified) {
                state.currentStep = RegistrationFlowStepVariantB.PHONE_VERIFICATION;
            }
        },
        nextStepVariantB: (state, action: PayloadAction<{hasCompleteProfile: boolean; hasPhoneVerified: boolean}>) => {
            switch (state.currentStep) {
                case RegistrationFlowStepVariantB.REGISTRATION:
                    if (!action.payload.hasCompleteProfile) {
                        state.currentStep = RegistrationFlowStepVariantB.PHONE_COMPLETION;
                    } else if (!action.payload.hasPhoneVerified) {
                        state.currentStep = RegistrationFlowStepVariantB.PHONE_VERIFICATION;
                    } else if (state.lastAuthAction === AuthActionType.LOGIN) {
                        return initialState;
                    } else {
                        state.currentStep = RegistrationFlowStepVariantB.PREFERENCES;
                    }
                    break;
                case RegistrationFlowStepVariantB.PHONE_COMPLETION:
                    if (!state.hasPhoneVerified) {
                        state.currentStep = RegistrationFlowStepVariantB.PHONE_VERIFICATION;
                    } else {
                        state.currentStep = RegistrationFlowStepVariantB.PREFERENCES;
                    }
                    break;
                case RegistrationFlowStepVariantB.PHONE_VERIFICATION:
                    state.currentStep = RegistrationFlowStepVariantB.PREFERENCES;
                    break;
                case RegistrationFlowStepVariantB.PREFERENCES:
                    state.currentStep = RegistrationFlowStepVariantB.UPDATED_PREFERENCES;
                    break;
                default:
                    state.isActive = false;
            }
        },
        setStepVariantB: (state, action: PayloadAction<{step: RegistrationFlowStepVariantB}>) => {
            state.currentStep = action.payload.step;
        },
        savePreferencesVariantB: (state, action: PayloadAction<IRegistrationFlowPreferences>) => {
            state.preferences = action.payload;
        },
        closeFlowVariantB: () => {
            return initialState;
        },
        setLastAuthActionVariantB: (state, action: PayloadAction<{authAction: AuthActionType}>) => {
            const {authAction} = action.payload;
            state.lastAuthAction = authAction;
        },
        setRegistrationMethodVariantB: (state, action: PayloadAction<{method: RegistrationMethod}>) => {
            state.registrationMethod = action.payload.method;
        },
        setPropertyAndOfferIds: (state, action: PayloadAction<{propertyId?: number; offerId?: number}>) => {
            state.propertyId = action.payload.propertyId;
            state.offerId = action.payload.offerId;
        }
    }
});

export const {
    startRegistrationFlowVariantB,
    nextStepVariantB,
    setStepVariantB,
    savePreferencesVariantB,
    closeFlowVariantB,
    setLastAuthActionVariantB,
    setRegistrationMethodVariantB,
    setPropertyAndOfferIds
} = registrationFlowVariantBSlice.actions;
